import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, Globe, Sparkles, User, Save, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useAuthStore, useUIStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user, updateSettings } = useAuthStore();
  const { language, setLanguage, mode, setMode } = useUIStore();
  const { t } = useTranslation(language);
  
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    language: language,
    mode: mode,
  });

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await updateSettings({
        name: formData.name,
        language: formData.language,
        mode: formData.mode,
      });
      setLanguage(formData.language);
      setMode(formData.mode);
      toast.success(language === 'hi' ? 'सेटिंग्स सेव हो गईं' : 'Settings saved');
    } catch (error) {
      toast.error(error.message || t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Settings className="h-6 w-6 text-primary" />
          {t('settings')}
        </h1>
        <p className="text-muted-foreground mt-1">
          {language === 'hi' ? 'अपनी प्राथमिकताएं कॉन्फ़िगर करें' : 'Configure your preferences'}
        </p>
      </div>

      {/* Profile Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="glass">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <User className="h-5 w-5" />
              {language === 'hi' ? 'प्रोफ़ाइल' : 'Profile'}
            </CardTitle>
            <CardDescription>
              {language === 'hi' ? 'अपनी प्रोफ़ाइल जानकारी अपडेट करें' : 'Update your profile information'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t('name')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-secondary/50 border-white/10 max-w-sm"
                data-testid="settings-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('email')}</Label>
              <Input
                value={user?.email || ''}
                disabled
                className="bg-secondary/50 border-white/10 max-w-sm opacity-60"
              />
              <p className="text-xs text-muted-foreground">
                {language === 'hi' ? 'ईमेल बदला नहीं जा सकता' : 'Email cannot be changed'}
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Language Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="glass">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Globe className="h-5 w-5" />
              {t('language_setting')}
            </CardTitle>
            <CardDescription>
              {language === 'hi' ? 'अपनी पसंदीदा भाषा चुनें' : 'Choose your preferred language'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={formData.language}
              onValueChange={(value) => setFormData({ ...formData, language: value })}
              className="flex flex-col space-y-3"
            >
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="en" id="lang-en" data-testid="lang-en-radio" />
                <Label htmlFor="lang-en" className="flex items-center gap-2 cursor-pointer">
                  <span className="text-lg">🇺🇸</span>
                  English
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="hi" id="lang-hi" data-testid="lang-hi-radio" />
                <Label htmlFor="lang-hi" className="flex items-center gap-2 cursor-pointer">
                  <span className="text-lg">🇮🇳</span>
                  हिन्दी (Hindi)
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="es" id="lang-es" data-testid="lang-es-radio" />
                <Label htmlFor="lang-es" className="flex items-center gap-2 cursor-pointer">
                  <span className="text-lg">🇪🇸</span>
                  Español (Spanish)
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="ar" id="lang-ar" data-testid="lang-ar-radio" />
                <Label htmlFor="lang-ar" className="flex items-center gap-2 cursor-pointer">
                  <span className="text-lg">🇸🇦</span>
                  العربية (Arabic)
                </Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>
      </motion.div>

      {/* Mode Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="glass">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              {t('mode_setting')}
            </CardTitle>
            <CardDescription>
              {language === 'hi' ? 'अपना इंटरफ़ेस मोड चुनें' : 'Choose your interface mode'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={formData.mode}
              onValueChange={(value) => setFormData({ ...formData, mode: value })}
              className="grid grid-cols-2 gap-4"
            >
              <Label
                htmlFor="mode-simple"
                className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.mode === 'simple'
                    ? 'border-primary bg-primary/10'
                    : 'border-white/10 hover:border-white/20'
                }`}
              >
                <RadioGroupItem value="simple" id="mode-simple" className="sr-only" data-testid="mode-simple-radio" />
                <Sparkles className={`h-8 w-8 ${formData.mode === 'simple' ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className="text-center">
                  <p className="font-medium">{t('simple_mode')}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t('simple_mode_desc')}</p>
                </div>
              </Label>
              
              <Label
                htmlFor="mode-advanced"
                className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.mode === 'advanced'
                    ? 'border-primary bg-primary/10'
                    : 'border-white/10 hover:border-white/20'
                }`}
              >
                <RadioGroupItem value="advanced" id="mode-advanced" className="sr-only" data-testid="mode-advanced-radio" />
                <Settings className={`h-8 w-8 ${formData.mode === 'advanced' ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className="text-center">
                  <p className="font-medium">{t('advanced_mode')}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t('advanced_mode_desc')}</p>
                </div>
              </Label>
            </RadioGroup>
          </CardContent>
        </Card>
      </motion.div>

      {/* Save Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex justify-end"
      >
        <Button
          onClick={handleSave}
          disabled={isLoading}
          className="glow-primary"
          data-testid="save-settings-btn"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {t('save_settings')}
        </Button>
      </motion.div>
    </div>
  );
};

export default SettingsPage;
